import { CatalogOccasion, CatalogRegion } from './occasion-catalog'

// Which of a country's occasions a subscription actually wants. Pure — no
// storage and no baseline — so the rule can be read, and tested, on its own. The
// store that holds subscriptions is services/country-occasions.ts.
//
// A country is subscribed whole and stays that way, so an occasion added to the
// catalog next year arrives without anyone doing anything. What the user
// switched off is kept as a list of exceptions, never as a frozen list of what
// to keep: that was an earlier shape, and hiding one day silently stopped every
// future one from ever appearing.
//
// Regions are the other axis. Absent means every region — "add all". An empty
// list means nationwide occasions only. A list of codes means nationwide plus
// those regions (and anything nested under them, so Massachusetts includes
// Suffolk County). A new holiday in an unselected state does not appear.

export interface ICountrySubscription {
    code: string
    // Occasion ids switched off by hand.
    hidden: string[]
    // Absent = every region. Empty = nationwide only. Otherwise nationwide
    // plus these region codes.
    regions?: string[]
}

export function descendantCodes(code: string, regions: CatalogRegion[]): string[] {
    const out = [code]
    let grew = true
    while (grew) {
        grew = false
        for (const r of regions) {
            if (r.parent && out.includes(r.parent) && !out.includes(r.code)) {
                out.push(r.code)
                grew = true
            }
        }
    }
    return out
}

export function topLevelOf(code: string, regions: CatalogRegion[]): string {
    const found = regions.find(r => r.code === code)
    return found?.parent ? topLevelOf(found.parent, regions) : code
}

// Is this the country's own day rather than one region's?
//
// Two taggings mean the same thing and the catalog uses both. America's federal
// holidays and Australia's national ones carry no region at all. Britain's bank
// holidays name all three nations, and Germany's New Year names all four
// researched states, because that is how their sources write them. An occasion
// that covers every top-level region is not "three regions" — it is the whole
// country — so both forms have to read the same way here. They did not, and
// nine of Britain's seventeen days disappeared from a subscription that had
// asked for no region.
export function coversEveryRegion(
    occasion: Pick<CatalogOccasion, 'regions'>,
    catalogRegions: CatalogRegion[],
): boolean {
    const regions = occasion.regions
    if (!regions?.length) return true
    const tops = catalogRegions.filter(r => !r.parent)
    if (!tops.length) return true
    const covered = new Set(regions.map(r => topLevelOf(r, catalogRegions)))
    return tops.every(t => covered.has(t.code))
}

// Region gating on its own, without the by-hand exceptions. The country page
// lists the occasions of the regions it is showing and then draws a switch on
// each row, so it asks this question without the answer to the other one.
export const inSelectedRegion = (
    occasion: Pick<CatalogOccasion, 'regions'>,
    regions: string[] | undefined,
    catalogRegions: CatalogRegion[] = [],
): boolean => {
    if (regions === undefined) return true
    if (coversEveryRegion(occasion, catalogRegions)) return true
    if (!regions.length) return false
    const selected = new Set(regions.flatMap(code => descendantCodes(code, catalogRegions)))
    return (occasion.regions ?? []).some(r => selected.has(r))
}

export const isVisible = (
    occasion: Pick<CatalogOccasion, 'id' | 'regions'>,
    sub: ICountrySubscription,
    catalogRegions: CatalogRegion[] = [],
): boolean =>
    !sub.hidden.includes(occasion.id)
    && inSelectedRegion(occasion, sub.regions, catalogRegions)

// A state holiday that occupies the same event as a nationwide one — Native
// Americans' Day standing in for Columbus Day in South Dakota — drops the
// replaced id so the day is not listed twice.
export function withoutReplaced<T extends Pick<CatalogOccasion, 'id' | 'replaces'>>(
    occasions: T[],
): T[] {
    const replaced = new Set(
        occasions.map(o => o.replaces).filter((id): id is string => !!id))
    return occasions.filter(o => !replaced.has(o.id))
}

export function shownIds(
    definitions: CatalogOccasion[],
    sub: ICountrySubscription,
    catalogRegions: CatalogRegion[] = [],
): Set<string> {
    return new Set(
        withoutReplaced(definitions.filter(d => isVisible(d, sub, catalogRegions)))
            .map(d => d.id))
}
